import { useEffect, useRef, useMemo, useState } from 'react';
import {
  Heading1,
  Heading2,
  Heading3,
  List,
  ListOrdered,
  Quote,
  Code,
  Image as ImageIcon,
  Video,
  Music,
  FileText,
  Table,
  Minus,
  Info,
  CheckSquare,
  Type,
  ChevronRight,
  Columns,
  TableOfContents,
  LinkIcon
} from 'lucide-react';
import './scss/SlashCommandPlugin.scss';

// Command options
export const SLASH_COMMANDS = [
  // Basic blocks
  {
    key: 'paragraph',
    label: 'Text',
    icon: 'Type',
    description: 'Just start writing with plain text',
    keywords: ['paragraph', 'text', 'normal'],
    category: 'Basic blocks',
    shortcut: null,
  },
  {
    key: 'h1',
    label: 'Heading 1',
    icon: 'Heading1',
    description: 'Big section heading',
    keywords: ['heading', 'h1', 'title'],
    category: 'Basic blocks',
    shortcut: '#',
  },
  {
    key: 'h2',
    label: 'Heading 2',
    icon: 'Heading2',
    description: 'Medium section heading',
    keywords: ['heading', 'h2', 'subtitle'],
    category: 'Basic blocks',
    shortcut: '##',
  },
  {
    key: 'h3',
    label: 'Heading 3',
    icon: 'Heading3',
    description: 'Small section heading',
    keywords: ['heading', 'h3'],
    category: 'Basic blocks',
    shortcut: '###',
  },
  {
    key: 'bullet',
    label: 'Bulleted List',
    icon: 'List',
    description: 'Create a simple bulleted list',
    keywords: ['bullet', 'list', 'ul', 'unordered'],
    category: 'Basic blocks',
    shortcut: '-',
  },
  {
    key: 'number',
    label: 'Numbered List',
    icon: 'ListOrdered',
    description: 'Create a list with numbering',
    keywords: ['number', 'list', 'ol', 'ordered'],
    category: 'Basic blocks',
    shortcut: '1.',
  },
  {
    key: 'checklist',
    label: 'To-do List',
    icon: 'CheckSquare',
    description: 'Track tasks with a to-do list',
    keywords: ['checklist', 'todo', 'task', 'checkbox'],
    category: 'Basic blocks',
    shortcut: '[]',
  },
  {
    key: 'toggle',
    label: 'Toggle List',
    icon: 'ChevronRight',
    description: 'Collapsible content list',
    keywords: ['toggle', 'collapsible', 'hide', 'accordion'],
    category: 'Basic blocks',
    shortcut: '>>',
  },
  {
    key: 'quote',
    label: 'Quote',
    icon: 'Quote',
    description: 'Capture a quote',
    keywords: ['quote', 'blockquote'],
    category: 'Basic blocks',
    shortcut: '>',
  },
  {
    key: 'divider',
    label: 'Divider',
    icon: 'Minus',
    description: 'Visually divide blocks',
    keywords: ['divider', 'separator', 'line', 'hr', 'horizontal rule'],
    category: 'Basic blocks',
    shortcut: '---',
  },
  {
    key: 'table',
    label: 'Table',
    icon: 'Table',
    description: 'Insert a table',
    keywords: ['table', 'grid', 'spreadsheet'],
    category: 'Basic blocks',
    shortcut: null,
  },
  {
    key: 'callout-info',
    label: 'Callout',
    icon: 'Info',
    description: 'Make writing stand out',
    keywords: ['callout', 'info', 'note', 'alert'],
    category: 'Basic blocks',
    shortcut: null,
  },

  // Media
  {
    key: 'image',
    label: 'Image',
    icon: 'ImageIcon',
    description: 'Upload or embed with a link',
    keywords: ['image', 'picture', 'photo'],
    category: 'Media',
    shortcut: null,
  },
  {
    key: 'video',
    label: 'Video',
    icon: 'Video',
    description: 'Embed from YouTube, Vimeo, etc.',
    keywords: ['video', 'youtube', 'vimeo', 'movie'],
    category: 'Media',
    shortcut: null,
  },
  {
    key: 'audio',
    label: 'Audio',
    icon: 'Music',
    description: 'Embed from Spotify, Soundcloud, etc.',
    keywords: ['audio', 'music', 'sound', 'spotify'],
    category: 'Media',
    shortcut: null,
  },
  {
    key: 'code',
    label: 'Code',
    icon: 'Code',
    description: 'Capture a code snippet',
    keywords: ['code', 'codeblock'],
    category: 'Media',
    shortcut: '```',
  },
  {
    key: 'file',
    label: 'File',
    icon: 'FileText',
    description: 'Upload a file or document',
    keywords: ['file', 'attachment', 'pdf'],
    category: 'Media',
    shortcut: null,
  },

  // Advanced
  {
    key: 'page',
    label: 'Link to page',
    icon: 'LinkIcon',
    description: 'Embed a sub-page in this page',
    keywords: ['page', 'subpage', 'nest', 'link'],
    category: 'Advanced',
    shortcut: null,
  },
  {
    key: 'columns-2',
    label: '2 Columns',
    icon: 'Columns',
    description: 'Split into two columns',
    keywords: ['column', 'columns', 'layout', 'split', '2'],
    category: 'Advanced',
    shortcut: null,
  },
  {
    key: 'columns-3',
    label: '3 Columns',
    icon: 'Columns',
    description: 'Split into three columns',
    keywords: ['column', 'columns', 'layout', 'split', '3'],
    category: 'Advanced',
    shortcut: null,
  },
  {
    key: 'columns-4',
    label: '4 Columns',
    icon: 'Columns',
    description: 'Split into four columns',
    keywords: ['column', 'columns', 'layout', 'split', '4'],
    category: 'Advanced',
    shortcut: null,
  },
  {
    key: 'toc',
    label: 'Table of Contents',
    icon: 'TableOfContents',
    description: 'Auto-generated from headings',
    keywords: ['toc', 'table of contents', 'contents', 'navigation', 'outline'],
    category: 'Advanced',
    shortcut: null,
  }
];

const IconMap = {
  Heading1,
  Heading2,
  Heading3,
  List,
  ListOrdered,
  Quote,
  Code,
  ImageIcon,
  Video,
  Music,
  FileText,
  Table,
  Minus,
  Info,
  CheckSquare,
  Type,
  ChevronRight,
  Columns,
  TableOfContents,
  LinkIcon
};

// Suggested items shown at the top (commonly used blocks)
const SUGGESTED_KEYS = ['checklist', 'page', 'h1', 'table'];

// Preview mockups for block types
function BlockPreviewMockup({ blockKey }) {
  const mockups = {
    'paragraph': (
      <div className="mockup-block">
        <div className="mockup-line" style={{ width: '100%' }} />
        <div className="mockup-line" style={{ width: '85%' }} />
        <div className="mockup-line" style={{ width: '60%' }} />
      </div>
    ),
    'h1': (
      <div className="mockup-block">
        <div className="mockup-heading mockup-h1">Heading 1</div>
        <div className="mockup-line" style={{ width: '90%' }} />
        <div className="mockup-line" style={{ width: '70%' }} />
      </div>
    ),
    'h2': (
      <div className="mockup-block">
        <div className="mockup-heading mockup-h2">Heading 2</div>
        <div className="mockup-line" style={{ width: '85%' }} />
        <div className="mockup-line" style={{ width: '60%' }} />
      </div>
    ),
    'h3': (
      <div className="mockup-block">
        <div className="mockup-heading mockup-h3">Heading 3</div>
        <div className="mockup-line" style={{ width: '80%' }} />
        <div className="mockup-line" style={{ width: '55%' }} />
      </div>
    ),
    'bullet': (
      <div className="mockup-block">
        <div className="mockup-list-item"><span className="mockup-bullet" /><div className="mockup-line" style={{ width: '80%' }} /></div>
        <div className="mockup-list-item"><span className="mockup-bullet" /><div className="mockup-line" style={{ width: '65%' }} /></div>
        <div className="mockup-list-item"><span className="mockup-bullet" /><div className="mockup-line" style={{ width: '50%' }} /></div>
      </div>
    ),
    'number': (
      <div className="mockup-block">
        <div className="mockup-list-item"><span className="mockup-number">1.</span><div className="mockup-line" style={{ width: '80%' }} /></div>
        <div className="mockup-list-item"><span className="mockup-number">2.</span><div className="mockup-line" style={{ width: '65%' }} /></div>
        <div className="mockup-list-item"><span className="mockup-number">3.</span><div className="mockup-line" style={{ width: '50%' }} /></div>
      </div>
    ),
    'checklist': (
      <div className="mockup-block">
        <div className="mockup-list-item"><span className="mockup-checkbox checked" /><div className="mockup-line mockup-strikethrough" style={{ width: '70%' }} /></div>
        <div className="mockup-list-item"><span className="mockup-checkbox" /><div className="mockup-line" style={{ width: '60%' }} /></div>
        <div className="mockup-list-item"><span className="mockup-checkbox" /><div className="mockup-line" style={{ width: '55%' }} /></div>
      </div>
    ),
    'toggle': (
      <div className="mockup-block">
        <div className="mockup-toggle"><span className="mockup-toggle-arrow">▶</span><div className="mockup-line" style={{ width: '70%' }} /></div>
        <div className="mockup-toggle-content">
          <div className="mockup-line" style={{ width: '85%' }} />
          <div className="mockup-line" style={{ width: '60%' }} />
        </div>
      </div>
    ),
    'quote': (
      <div className="mockup-block">
        <div className="mockup-quote">
          <div className="mockup-line" style={{ width: '90%' }} />
          <div className="mockup-line" style={{ width: '70%' }} />
        </div>
      </div>
    ),
    'divider': (
      <div className="mockup-block">
        <div className="mockup-line" style={{ width: '60%' }} />
        <div className="mockup-divider" />
        <div className="mockup-line" style={{ width: '55%' }} />
      </div>
    ),
    'table': (
      <div className="mockup-block">
        <div className="mockup-table">
          <div className="mockup-table-row mockup-table-header"><div className="mockup-table-cell" /><div className="mockup-table-cell" /><div className="mockup-table-cell" /></div>
          <div className="mockup-table-row"><div className="mockup-table-cell" /><div className="mockup-table-cell" /><div className="mockup-table-cell" /></div>
          <div className="mockup-table-row"><div className="mockup-table-cell" /><div className="mockup-table-cell" /><div className="mockup-table-cell" /></div>
        </div>
      </div>
    ),
    'callout-info': (
      <div className="mockup-block">
        <div className="mockup-callout">
          <span className="mockup-callout-icon">💡</span>
          <div style={{ flex: 1 }}>
            <div className="mockup-line" style={{ width: '85%' }} />
            <div className="mockup-line" style={{ width: '60%' }} />
          </div>
        </div>
      </div>
    ),
    'code': (
      <div className="mockup-block">
        <div className="mockup-code">
          <div className="mockup-line mockup-code-line" style={{ width: '45%' }} />
          <div className="mockup-line mockup-code-line" style={{ width: '70%', marginLeft: 10 }} />
          <div className="mockup-line mockup-code-line" style={{ width: '55%', marginLeft: 10 }} />
          <div className="mockup-line mockup-code-line" style={{ width: '30%' }} />
        </div>
      </div>
    ),
    'image': (
      <div className="mockup-block">
        <div className="mockup-image">
          <ImageIcon size={20} strokeWidth={1.2} />
        </div>
      </div>
    ),
    'columns-2': (
      <div className="mockup-block">
        <div className="mockup-columns">
          <div className="mockup-col"><div className="mockup-line" style={{ width: '90%' }} /><div className="mockup-line" style={{ width: '70%' }} /></div>
          <div className="mockup-col"><div className="mockup-line" style={{ width: '80%' }} /><div className="mockup-line" style={{ width: '60%' }} /></div>
        </div>
      </div>
    ),
    'columns-3': (
      <div className="mockup-block">
        <div className="mockup-columns">
          <div className="mockup-col"><div className="mockup-line" style={{ width: '90%' }} /></div>
          <div className="mockup-col"><div className="mockup-line" style={{ width: '80%' }} /></div>
          <div className="mockup-col"><div className="mockup-line" style={{ width: '85%' }} /></div>
        </div>
      </div>
    ),
    'columns-4': (
      <div className="mockup-block">
        <div className="mockup-columns">
          <div className="mockup-col"><div className="mockup-line" style={{ width: '90%' }} /></div>
          <div className="mockup-col"><div className="mockup-line" style={{ width: '80%' }} /></div>
          <div className="mockup-col"><div className="mockup-line" style={{ width: '85%' }} /></div>
          <div className="mockup-col"><div className="mockup-line" style={{ width: '75%' }} /></div>
        </div>
      </div>
    ),
    'toc': (
      <div className="mockup-block">
        <div className="mockup-toc">
          <div className="mockup-toc-title">Table of Contents</div>
          <div className="mockup-toc-item"><div className="mockup-line" style={{ width: '65%' }} /></div>
          <div className="mockup-toc-item" style={{ paddingLeft: 10 }}><div className="mockup-line" style={{ width: '55%' }} /></div>
          <div className="mockup-toc-item" style={{ paddingLeft: 10 }}><div className="mockup-line" style={{ width: '50%' }} /></div>
        </div>
      </div>
    ),
  };

  return mockups[blockKey] || (
    <div className="mockup-block">
      <div className="mockup-line" style={{ width: '80%' }} />
      <div className="mockup-line" style={{ width: '60%' }} />
    </div>
  );
}

// Menu component for Redux popover
const SlashCommandMenu = ({ options, selectedIndex, onSelect, onMouseEnter, isFiltering = false }) => {
  const selectedItemRef = useRef(null);
  const menuRef = useRef(null);
  const isMouseHover = useRef(false);
  const [hoveredItem, setHoveredItem] = useState(null);

  // Group options by category, with "Suggested" at top
  const groupedOptions = useMemo(() => {
    if (isFiltering) return null;

    const suggested = SUGGESTED_KEYS
      .map(key => options.find(o => o.key === key))
      .filter(Boolean);

    const groups = { 'Suggested': suggested };

    options.forEach(option => {
      const category = option.category || 'Other';
      if (!groups[category]) {
        groups[category] = [];
      }
      groups[category].push(option);
    });

    return groups;
  }, [options, isFiltering]);

  // Scroll selected item into view only on keyboard navigation (not mouse hover)
  useEffect(() => {
    if (isMouseHover.current) {
      isMouseHover.current = false;
      return;
    }

    if (selectedItemRef.current && menuRef.current) {
      const menuElement = menuRef.current.querySelector('.slash-command-list');
      const selectedElement = selectedItemRef.current;

      if (!menuElement) return;

      const menuRect = menuElement.getBoundingClientRect();
      const selectedRect = selectedElement.getBoundingClientRect();

      const isAboveView = selectedRect.top < menuRect.top + 8;
      const isBelowView = selectedRect.bottom > menuRect.bottom;

      if (isAboveView || isBelowView) {
        selectedElement.scrollIntoView({
          behavior: 'instant',
          block: 'nearest',
        });
      }
    }
  }, [selectedIndex]);

  const renderOption = (option, index, isSuggested = false) => {
    const Icon = IconMap[option.icon];

    return (
      <li
        key={isSuggested ? `suggested-${option.key}` : option.key}
        ref={selectedIndex === index && !isSuggested ? selectedItemRef : null}
        tabIndex={-1}
        className={`slash-command-item${selectedIndex === index ? ' selected' : ''}`}
        role="option"
        aria-selected={selectedIndex === index}
        onMouseEnter={() => {
          isMouseHover.current = true;
          setHoveredItem(option);
          onMouseEnter(index);
        }}
        onMouseLeave={() => setHoveredItem(null)}
        onClick={() => onSelect(option)}
      >
        <div className="slash-command-icon-wrapper">
          {Icon && <Icon size={14} strokeWidth={1.8} />}
        </div>
        <span className="slash-command-label">{option.label}</span>
        {option.shortcut && (
          <span className="slash-command-shortcut">{option.shortcut}</span>
        )}
      </li>
    );
  };

  return (
    <div className="slash-command-container" ref={menuRef}>
      <div className="slash-command-menu">
        <div className="slash-command-list">
          {isFiltering ? (
            <div className="slash-command-section">
              <div className="slash-command-section-header">Filtered results</div>
              <ul>
                {options.map((option, i) => renderOption(option, i))}
              </ul>
            </div>
          ) : (
            Object.entries(groupedOptions).map(([category, categoryOptions]) => {
              const isSuggested = category === 'Suggested';
              return (
                <div key={category} className="slash-command-section">
                  <div className="slash-command-section-header">{category}</div>
                  <ul>
                    {categoryOptions.map((option) => {
                      const index = options.findIndex(o => o.key === option.key);
                      return renderOption(option, index, isSuggested);
                    })}
                  </ul>
                </div>
              );
            })
          )}
        </div>

        <div className="slash-command-footer">
          <span className="footer-label">Close menu</span>
          <span className="footer-shortcut">esc</span>
        </div>
      </div>

      {hoveredItem && (
        <div className="slash-command-preview">
          <div className="preview-mockup">
            <BlockPreviewMockup blockKey={hoveredItem.key} />
          </div>
          <div className="preview-description">{hoveredItem.description}</div>
        </div>
      )}
    </div>
  );
};

export default SlashCommandMenu;
